import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { dateShort, money } from "../lib/format";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import {
  AlertCircle,
  ArrowUpRight,
  Loader2,
  PackageSearch,
} from "lucide-react";

const STATUS_COPY = {
  draft: { label: "Draft", cls: "text-[#525252] bg-[#FAFAFA] border-[#E5E5E5]" },
  extracting: {
    label: "Extracting",
    cls: "text-[#CA8A04] bg-[#FEFCE8] border-[#FEF08A]",
  },
  ready: {
    label: "Ready",
    cls: "text-[#16A34A] bg-[#F0FDF4] border-[#BBF7D0]",
  },
  error: {
    label: "Error",
    cls: "text-[#DC2626] bg-[#FFF0F0] border-[#FECACA]",
  },
};

export default function ReportsList() {
  const [reports, setReports] = useState(null);
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const load = async () => {
    try {
      const data = await api.listReports();
      // Store owners have a single continuous ledger — skip the list and
      // jump straight to their report dashboard. Admins stay on the list
      // so they can pick which store to inspect.
      if (!isAdmin && data.length >= 1) {
        navigate(`/reports/${data[0].id}`, { replace: true });
        return;
      }
      setReports(data);
    } catch (e) {
      toast.error("Failed to load reports");
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  return (
    <div data-testid="reports-list-page">
      <div className="flex items-start justify-between gap-6 mb-8">
        <div>
          <div className="eyebrow mb-3">
            {isAdmin ? "All stores" : "Reconciliation"}
          </div>
          <h1 className="font-display text-3xl md:text-4xl font-bold leading-tight">
            {isAdmin
              ? "Store reconciliation reports"
              : "Audit every UPS invoice against your CMS."}
          </h1>
          <p className="text-[#525252] text-sm mt-4 max-w-2xl leading-relaxed">
            {isAdmin
              ? "Every tenant's reconciliation ledger. Click a row to inspect that store's reconciliation, flagged shipments, and uploads."
              : "Upload your CMS transaction reports and UPS delivery invoices. We flag shipments UPS billed without a matching CMS sale, and lines where UPS charged more than you collected."}
          </p>
        </div>
      </div>

      {reports === null ? (
        <div className="flex items-center gap-2 text-[#525252] text-sm">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading reports…
        </div>
      ) : reports.length === 0 ? (
        <EmptyState isAdmin={isAdmin} />
      ) : (
        <div className="bg-white border border-[#E5E5E5] rounded-md overflow-hidden">
          <table
            className="w-full data-table text-sm"
            data-testid="reports-table"
          >
            <thead>
              <tr>
                {isAdmin && (
                  <th className="px-5 py-3 text-left">Store</th>
                )}
                <th className="px-5 py-3 text-left">Report</th>
                <th className="px-5 py-3 text-left">Status</th>
                <th className="px-5 py-3 text-right">Charged</th>
                <th className="px-5 py-3 text-right">UPS cost</th>
                <th className="px-5 py-3 text-right">Profit</th>
                <th className="px-5 py-3 text-right">Flagged</th>
                <th className="px-5 py-3 text-left">Reconciled</th>
                <th className="px-5 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {reports.map((r) => {
                const flagged =
                  (r.summary?.overcharge_count || 0) +
                  (r.summary?.missing_from_cms_count || 0);
                const sBadge = STATUS_COPY[r.status] || STATUS_COPY.draft;
                return (
                  <tr
                    key={r.id}
                    className="border-t border-[#E5E5E5] hover:bg-[#FAFAFA] cursor-pointer"
                    onClick={() => navigate(`/reports/${r.id}`)}
                    data-testid={`report-row-${r.id}`}
                  >
                    {isAdmin && (
                      <td className="px-5 py-4">
                        <div className="font-medium">
                          {r.store_name || "—"}
                        </div>
                        <div className="text-[11px] font-mono-tabular text-[#525252] mt-0.5">
                          #{r.store_number || "?"}
                          {r.store_email ? ` · ${r.store_email}` : ""}
                        </div>
                      </td>
                    )}
                    <td className="px-5 py-4 text-[#525252]">{r.period}</td>
                    <td className="px-5 py-4">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-sm text-[11px] font-medium border ${sBadge.cls}`}
                      >
                        {sBadge.label}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-right font-mono-tabular">
                      {money(r.summary?.total_charged_customer)}
                    </td>
                    <td className="px-5 py-4 text-right font-mono-tabular">
                      {money(r.summary?.total_ups_cost)}
                    </td>
                    <td
                      className={`px-5 py-4 text-right font-mono-tabular font-medium ${
                        (r.summary?.total_profit || 0) < 0
                          ? "text-[#DC2626]"
                          : ""
                      }`}
                    >
                      {money(r.summary?.total_profit)}
                    </td>
                    <td className="px-5 py-4 text-right">
                      {flagged > 0 ? (
                        <span className="inline-flex items-center gap-1.5 text-[#DC2626] font-mono-tabular">
                          <AlertCircle className="w-3.5 h-3.5" />
                          {flagged}
                        </span>
                      ) : (
                        <span className="text-[#525252] font-mono-tabular">
                          0
                        </span>
                      )}
                    </td>
                    <td className="px-5 py-4 text-[#525252] font-mono-tabular text-xs">
                      {r.reconciled_at ? dateShort(r.reconciled_at) : "—"}
                    </td>
                    <td className="px-5 py-4 text-right">
                      <Link
                        to={`/reports/${r.id}`}
                        onClick={(e) => e.stopPropagation()}
                        className="inline-flex p-1.5 rounded-sm hover:bg-[#F0F0EF] text-[#525252]"
                        data-testid={`open-report-${r.id}`}
                        aria-label="Open report"
                      >
                        <ArrowUpRight className="w-4 h-4" />
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function EmptyState({ isAdmin }) {
  return (
    <div
      className="border border-[#E5E5E5] bg-white rounded-md p-14 text-center max-w-2xl mx-auto"
      data-testid="empty-state"
    >
      <div className="w-14 h-14 mx-auto bg-[#FAFAFA] border border-[#E5E5E5] rounded-md grid place-items-center mb-5">
        <PackageSearch className="w-6 h-6 text-[#525252]" />
      </div>
      <h3 className="font-display text-xl font-semibold mb-2">
        {isAdmin ? "No store reports yet" : "Setting up your ledger…"}
      </h3>
      <p className="text-sm text-[#525252] max-w-md mx-auto">
        {isAdmin
          ? "Once a store owner signs up and uploads their first CMS export or UPS invoice, their reconciliation will appear here."
          : "We're preparing your reconciliation report. If this persists, refresh the page or contact your admin."}
      </p>
    </div>
  );
}
